<template>
  <NModal :auto-focus="false"
          :close-on-esc="!backable && dismissable && !model.loading"
          :mask-closable="!backable && dismissable && !model.loading"
          :show="model.show"
          display-directive="show"
          class="rounded-t-2xl sm:rounded-2xl"
          transform-origin="center"
          @update-show="(v) => model.show = v"
  >
    <div :class="{
          'md:!max-w-6xl': fullscreen,
          'md:!max-w-4xl': wider,
          'overflow-y-auto': overflow,
         }"
         class="relative w-full max-h-[98%] md:max-h-[calc(100vh-96px)] md:max-w-lg"> 
         <!-- 96px is 48px top and bottom padding -->

      <div class="relative bg-neutral-50 shadow rounded-t-2xl sm:rounded-2xl dark:bg-neutral-700">

        <div class="sticky rounded-t-2xl sm:rounded-2xl top-0 z-50 bg-neutral-50 px-6 pt-6">
          <ModalsActionButton
              v-if="!backable && dismissable && !model.loading"
              type="close"
              @action="model.show = false"/>
          <ModalsActionButton
            v-if="backable && dismissable && !model.loading"
            type="back"
            @action="emits('back')"/>
          <div class="h-6"></div>
        </div>

        <div v-if="showHeader" :class="fullscreen ? 'hidden' : 'flex'"
             class="flex-col items-center justify-between px-6"
        >
          <div class="inline-flex flex-col items-center justify-center gap-3">
            <div v-if="icon || icon_text"
                 :class="icon_wrapper_classes"
                 class="h-10 w-10 bg-neutral-900 rounded-full flex justify-center items-center">
              <BaseIcon v-if="icon && !icon_text" 
                        :class="icon_classes"
                        :name="icon"
                        class="w-4 h-4 text-white"/>
              <span v-else>{{ icon_text }}</span>
            </div>

            <div class="flex flex-col items-start justify-start gap-1">
              <div v-if="title" class="self-stretch text-center text-lg font-medium leading-7 text-neutral-900">
                {{ $t(title) }}
              </div>
              <div v-if="subtitle"
                   class="self-stretch text-center text-sm font-normal leading-normal text-neutral-500">
                {{ $t(subtitle) }}
              </div>
            </div>

          </div>
        </div>

        <div v-if="modal" :class="fullscreen ? 'p-6 md:p-10' : ''"
             class="flex flex-col items-center justify-between px-6 pb-6">
              <component
                  :is="modal"
                  v-model="model"
                  :description="description"
                  class="w-full"
                  v-bind="$attrs"
              />
        </div>
      </div>
    </div>
  </NModal>
</template>
<script lang="ts" setup>
import {NModal} from "naive-ui"
import {extend} from "@vue/shared"

const component = extend({})
type VueComponent = InstanceType<typeof component>

const model = defineModel({ type: Object as PropType<ModalModel> })

const emits = defineEmits(['close', 'back'])

const props = defineProps({
  title: String,
  subtitle: String,
  modal: Object as VueComponent,
  description: String,
  icon: String,
  icon_classes: String,
  icon_wrapper_classes: String,
  icon_text: String,
  fullscreen: {
    type: Boolean,
    default: false
  },
  wider: {
    type: Boolean,
    default: false
  },
  dismissable: {
    type: Boolean,
    default: true
  },
  backable: {
    type: Boolean,
    default: false
  },
  error: {
    type: [String, null] as PropType<string | null>,
    default: null
  },
  overflow: {
    type: Boolean,
    default: true
  },
  showHeader: {
    type: Boolean,
    default: true
  }
})

const icon = ref(props.icon);
const title = ref(props.title);
const subtitle = ref(props.subtitle);
const icon_classes = ref(props.icon_classes);
const icon_wrapper_classes = ref(props.icon_wrapper_classes);
const dismissable = ref(props.dismissable );
const description = ref(props.description);
const icon_text = ref(props.icon_text);
const error = ref(props.error);

if (model.value) {
  watch(model.value, () => {
    icon.value = model.value?.icon || props.icon;
    title.value = model.value?.title || props.title;
    subtitle.value = model.value?.subtitle || props.subtitle;
    icon_classes.value = model.value?.icon_classes || props.icon_classes;
    icon_wrapper_classes.value = model.value?.icon_wrapper_classes || props.icon_wrapper_classes;
    dismissable.value = model.value?.dismissable || props.dismissable;
    description.value = model.value?.description || props.description;
    icon_text.value = model.value?.icon_text || props.icon_text;
    error.value = model.value?.error || props.error;
    emits('close', model.value?.show)
  })
}

</script>

<style>
@media (max-width: 639px) {
  .n-modal {
    position: fixed;
    bottom: 0;
  }

  .n-modal.fade-in-scale-up-transition-leave-active {
    transform-origin: inherit;
    transition: opacity .25s cubic-bezier(.4, 0, 1, 1), transform .25s;
  }

  .n-modal.fade-in-scale-up-transition-enter-active {
    transform-origin: inherit;
    transition: opacity .25s cubic-bezier(0, 0, .2, 1), transform .25s;
  }

  .n-modal.fade-in-scale-up-transition-enter-from, .n-modal.fade-in-scale-up-transition-leave-to {
    opacity: 1 !important;
    transform: translateY(100%) !important;
  }

  .n-modal.fade-in-scale-up-transition-leave-from, .n-modal.fade-in-scale-up-transition-enter-to {
    opacity: 1;
    transform: translateY(0) !important;
  }
}
</style>
